import { useTranslation } from "react-i18next";
import { ExternalLink, AlertTriangle, Smartphone, ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { StepCard } from "./StepCard";
import { SendTokenForm } from "./SendTokenForm";

interface OffRampLatamProps {
  currencyCode: string;
}

function OffRampLatam({ currencyCode }: OffRampLatamProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-gradient-to-r from-brand-500/10 to-success-500/10 p-4">
        <p
          className="text-sm text-text-secondary"
          dangerouslySetInnerHTML={{ __html: t("ramp.latamIntro", { currency: currencyCode }) }}
        />
      </div>

      <div className="space-y-5">
        <StepCard
          step={1}
          title={t("ramp.step1ElDorado")}
          description={t("ramp.step1ElDoradoDescLatam", { currency: currencyCode })}
          icon={<Smartphone className="h-4 w-4 text-brand-500" />}
          action={
            <div className="flex flex-wrap gap-2">
              <a href="https://app.eldorado.io" target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" size="sm" className="gap-1.5">
                  {t("ramp.openElDoradoWeb")}
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </a>
              <a href="https://play.google.com/store/apps/details?id=io.eldorado" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                  Google Play
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </a>
              <a href="https://apps.apple.com/app/el-dorado-p2p/id1576616116" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                  App Store
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </a>
            </div>
          }
        />

        <StepCard
          step={2}
          title={t("ramp.step2GetAddress")}
          description={t("ramp.step2GetAddressDescLatam")}
          icon={<ArrowRight className="h-4 w-4 text-brand-500" />}
        />

        <StepCard
          step={3}
          title={t("ramp.step3Send")}
          description={t("ramp.step3SendDesc")}
          icon={<Send className="h-4 w-4 text-brand-500" />}
          action={<SendTokenForm title={t("ramp.sendToElDorado")} />}
        />

        <StepCard
          step={4}
          title={t("ramp.step4SellCurrency", { currency: currencyCode })}
          description={t("ramp.step4SellCurrencyDesc", { currency: currencyCode })}
          icon={<ArrowRight className="h-4 w-4 text-brand-500" />}
        />
      </div>

      <div className="flex gap-2 rounded-xl border border-warning-200 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-950">
        <AlertTriangle className="h-4 w-4 shrink-0 text-warning-500" />
        <p
          className="text-xs text-warning-600 dark:text-warning-500"
          dangerouslySetInnerHTML={{ __html: t("ramp.warningPolygonShort") }}
        />
      </div>
    </div>
  );
}

export { OffRampLatam };
